/**
 * Rules for the Entra group to role mapping, mirroring the API's `validate` in
 * `services/role_groups.py` so the page can refuse a bad mapping in place and
 * say the same sentence the server would.
 */

import { APP_ROLES } from '../types'
import type { AppRole, DirectoryUser, GroupGrant, RoleGroupMapping } from '../types'

/** An Entra object ID. A pasted group NAME would never match the sign-in
 *  token's claim, and the mapping would look saved but never work. */
export const GROUP_ID = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/

export const NOT_A_GROUP_ID = "Use the group's object ID from Entra, not its name."
export const USER_IS_EVERYONE = 'Everyone who signs in is a User, so no group grants it.'

/** The roles a group can be mapped to: everything except `User`, which
 *  everyone holds already. */
export const MAPPABLE_ROLES: AppRole[] = APP_ROLES.filter(role => role !== 'User')

/** The first thing wrong with a mapping list, or null when it is savable. */
export function roleGroupsProblem(mappings: RoleGroupMapping[]): string | null {
  const seen = new Set<string>()
  for (const mapping of mappings) {
    if (!GROUP_ID.test((mapping.groupId || '').trim())) return NOT_A_GROUP_ID
    if (mapping.role === 'User') return USER_IS_EVERYONE
    if (!APP_ROLES.includes(mapping.role)) return `Unknown role: ${mapping.role || '(blank)'}.`
    const key = `${mapping.groupId.trim().toLowerCase()}|${mapping.role}`
    if (seen.has(key)) {
      return `The group ${mapping.groupName || mapping.groupId} is mapped to ${mapping.role} twice.`
    }
    seen.add(key)
  }
  return null
}

/** Every role a person's groups grant, most privileged first. `User` is
 *  implicit for everyone, so it is only listed alone. */
export function heldRoles(user: Pick<DirectoryUser, 'groupGrants'>): AppRole[] {
  const held = new Set<AppRole>((user.groupGrants ?? []).map((grant: GroupGrant) => grant.role))
  const ranked = APP_ROLES.filter(role => role !== 'User' && held.has(role))
  return ranked.length ? ranked : ['User']
}

/** Which groups gave someone a role, for the "where it came from" column. */
export function groupsBehind(
  user: Pick<DirectoryUser, 'groupGrants'>,
  role: AppRole,
  mappings: RoleGroupMapping[],
): RoleGroupMapping[] {
  const ids = (user.groupGrants ?? []).filter(grant => grant.role === role).map(grant => grant.groupId.toLowerCase())
  if (!ids.length) return []
  const known = mappings.filter(mapping => ids.includes(mapping.groupId.toLowerCase()))
  if (known.length) return known
  // The break-glass group is set in infrastructure, so it is never in the map.
  return ids.map(id => ({ groupId: id, groupName: '', role, scopes: [] }))
}

/** The scope values a person may edit within, for a role narrowed by scope. An
 *  empty list on any grant means all of them, and beats a narrower one. */
export function scopesFor(user: Pick<DirectoryUser, 'groupGrants'>, role: AppRole): string[] | 'all' {
  const grants = (user.groupGrants ?? []).filter(grant => grant.role === role)
  if (!grants.length) return []
  if (grants.some(grant => !grant.scopes?.length)) return 'all'
  return [...new Set(grants.flatMap(grant => grant.scopes))].sort()
}
